import React, { useEffect } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import {
  FormLabel,
  FormControl,
  FormItem,
  FormDescription,
  FormMessage,
} from "../../components/shadcn-ui/form";
import { Input } from "../../components/shadcn-ui/input";
import { Textarea } from "../../components/shadcn-ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/shadcn-ui/select";
import { MultiSelect } from "./MultiSelect";

type SelectFieldType = {
  type: "select" | "multiselect";
  options: string[];
};

type OtherThanOneSelectFieldType = {
  type: "text" | "email" | "tel" | "date" | "textarea";
  options?: never;
};

export type FieldType = {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "select" | "date" | "multiselect" | "textarea";
  validationRules: {
    required?: string | boolean;
    pattern?: { value: RegExp; message: string };
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    validate?: (value: any) => boolean | string;
    errorMessage?: string;
  };
  options?: string[]; // For select and multiselect fields
  description?: string;
  defaultValue?: string | string[];
  placeholder?: string;
} & (SelectFieldType | OtherThanOneSelectFieldType);

type DynamicFormFieldProps = {
  field: FieldType;
  isDisabled: boolean;
  handleChange?: (name: string, value: any) => void;
  // Accept additional input/select props, e.g. onBlur, onFocus, etc.
  extraProps?: any;
};

const renderInputComponent = ({
  type,
  label,
  options,
  defaultValue,
  field,
  isDisabled,
  hasError,
  handleChange,
  extraProps,
}: {
  type: string;
  label: string;
  options?: string[];
  defaultValue: string | string[] | undefined;
  field: any;
  isDisabled: boolean;
  hasError: boolean;
  handleChange?: (name: string, value: any) => void;
  extraProps?: any;
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (handleChange) {
      handleChange(name, value); // Call handleChange if provided
    } else {
      field.onChange(value); // Otherwise, use the field's default onChange
    }
  };

  const handleSelectChange = (value: string) => {
    const { name } = field;
    if (handleChange) {
      handleChange(name, value); // Call handleChange for select fields
    } else {
      field.onChange(value); // Use field's default onChange
    }
  };

  const handleMultiSelectChange = (values: string[]) => {
    const { name } = field;
    if (handleChange) {
      handleChange(name, values); // Call handleChange for multiselect fields
    } else {
      field.onChange(values); // Use field's default onChange
    }
  };

  switch (type) {
    case "text":
    case "email":
    case "tel":
      return (
        <Input
          type={type}
          value={defaultValue}
          placeholder={`Enter ${label}`}
          {...field}
          disabled={isDisabled}
          onChange={handleInputChange}
          {...extraProps}
          className={`placeholder:text-xs ${hasError ? "shake" : ""}`}
        />
      );
    case "select":
      return (
        <Select
          value={field.value || ""}
          onValueChange={handleSelectChange}
          disabled={isDisabled}
        >
          <SelectTrigger
            className={`h-8 w-full placeholder:text-xs ${
              hasError ? "shake" : ""
            }`}
          >
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent side="bottom">
            {options?.map((option: string, index: number) => (
              <SelectItem key={index} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "multiselect":
      return (
        <MultiSelect
          options={options || []}
          selected={Array.isArray(field.value) ? field.value : []}
          onChange={handleMultiSelectChange}
          placeholder={`Select ${label}`}
          disabled={isDisabled}
          className={hasError ? "shake" : ""}
        />
      );
    case "date":
    {
      // Parse field.value to yyyy-MM-dd format for the input
      const inputValue = field.value
          ? new Date(field.value).toISOString().split("T")[0] // Extract yyyy-MM-dd
          : "";

      return (
          <Input
              type="date"
              value={inputValue}
              disabled={isDisabled}
              onChange={(e) => {
                const { name, value } = e.target;
                // Convert back to ISO string (UTC) for backend
                const isoDate = value ? new Date(value + "T00:00:00Z").toISOString() : "";
                if (handleChange) {
                  handleChange(name, isoDate);
                } else {
                  field.onChange(isoDate);
                }
              }}
              {...extraProps}
              className={`placeholder:text-xs ${hasError ? "shake" : ""}`}
          />
      );
    }
    case "textarea":
      return (
        <Textarea
          placeholder={`Enter ${label}`}
          {...field}
          disabled={isDisabled}
          onChange={handleInputChange}
          {...extraProps}
          className={`placeholder:text-xs ${hasError ? "shake" : ""}`}
          rows={4}
        />
      );
    default:
      return (
        <Input
          placeholder={`Enter ${label}`}
          {...field}
          disabled={isDisabled}
          onChange={handleInputChange}
          {...extraProps}
          className={`placeholder:text-xs ${hasError ? "shake" : ""}`}
        />
      );
  }
};

const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  field,
  isDisabled,
  handleChange,
  extraProps,
}) => {
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();
  const { name, label, type, validationRules, options, description, defaultValue } = field;
  const hasError = !!errors[name];
  console.log("extraProps", extraProps);

  // Set default value when component mounts
  useEffect(() => {
    if (defaultValue !== undefined && !control._formValues[name]) {
      console.log(`Setting default value for ${name}:`, defaultValue);
      
      // Special handling for membershipDetails.id to prevent it from being auto-filled with application ID
      if (name === "membershipDetails.id" && defaultValue === control._formValues["id"]) {
        console.log("Skipping setting membershipDetails.id to match application ID");
        return;
      }
      
      setValue(name, defaultValue);
    }
  }, [name, defaultValue, setValue, control._formValues]);

  return (
    <FormItem className="space-y-1 basis-[100%] md:flex-wrap md:basis-[100%] items-center relative">
      <FormLabel className="relative">
        {label}
        {validationRules.required && (
          <span className="text-[16px] text-red-500 ml-[2px] absolute -right-[8px] -top-[0px]">
            *
          </span>
        )}
      </FormLabel>
      <FormControl>
        <Controller
          control={control}
          name={name}
          rules={validationRules}
          render={({ field: controllerField }) => (
            <>
              {renderInputComponent({
                type,
                label,
                options,
                defaultValue,
                field: controllerField,
                isDisabled,
                hasError,
                handleChange,
                extraProps,
              })}
            </>
          )}
        />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      {hasError && (
        <FormMessage color="error">
          {errors[name] && typeof errors[name] === "object"
            ? (errors[name] as FieldError).message || "This field is required"
            : "This field is required"}
        </FormMessage>
      )}
    </FormItem>
  );
};

export { DynamicFormField };
